import { useEffect } from 'react';
import { X } from 'lucide-react';
import SportsbookBetslipCta from './SportsbookBetslipCta';

type SportsbookBetslipPromptModalProps = {
  isOpen: boolean;
  onDismiss: () => void;
  /** Contest league code from `pickems.sport` (e.g. 'NFL') — picks the DK CMS page. */
  sport: string | null | undefined;
  /** Sportsbook selection ids for the outcomes in the player's submitted entry. */
  selectionIds: readonly (string | null | undefined)[];
  /** Player the pick is on, e.g. "D. Maye". Omitted when the pick has no name yet. */
  playerName?: string | null;
  /** Frozen prop line, e.g. "20+ Rush Yards". */
  propLabel?: string | null;
};

/**
 * One-shot prompt offering the DK Sportsbook handoff after a player submits a prop pick.
 *
 * Deliberately reuses {@link SportsbookBetslipCta} rather than rebuilding the link, so
 * the modal and the pick card share one deeplink code path — including the anchor-based
 * handoff that keeps the SPA alive (DKPLAT-778).
 *
 * Odds move between the pick being frozen and the betslip opening, so the copy promises
 * the selection is loaded, never a specific price.
 */
export default function SportsbookBetslipPromptModal({
  isOpen,
  onDismiss,
  sport,
  selectionIds,
  playerName,
  propLabel,
}: SportsbookBetslipPromptModalProps) {
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onDismiss]);

  if (!isOpen) return null;

  const selectionSummary = [playerName, propLabel].filter(Boolean).join(' · ');

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="sportsbook-betslip-prompt-title"
      data-test-id="sportsbook-betslip-prompt"
      // Backdrop tap dismisses; the guard stops a tap inside the card closing it.
      onClick={e => {
        if (e.target === e.currentTarget) onDismiss();
      }}
    >
      <div
        className="relative w-full max-w-[360px] rounded-xl border border-white/15 bg-[#1a1a1a] p-5 text-left"
        style={{ fontFamily: 'Inter, sans-serif' }}
      >
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Close bet slip prompt"
          data-test-id="sportsbook-betslip-prompt-close"
          className="absolute right-3 top-3 rounded-full p-1 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
        >
          <X size={20} />
        </button>

        <h2
          id="sportsbook-betslip-prompt-title"
          className="pr-8 text-[18px] font-bold uppercase tracking-wide text-white"
        >
          Back this pick for real?
        </h2>

        {selectionSummary ? (
          <p className="mt-2 text-[14px] font-semibold text-white/90">{selectionSummary}</p>
        ) : null}

        <p className="mt-2 text-[13px] leading-[18px] text-white/70">
          Send this selection straight to your DraftKings Sportsbook bet slip. Odds are live, so the
          price you see there may differ from your pick.
        </p>

        <SportsbookBetslipCta
          sport={sport}
          selectionIds={selectionIds}
          onNavigate={onDismiss}
          className="mt-4"
        />

        <button
          type="button"
          onClick={onDismiss}
          data-test-id="sportsbook-betslip-prompt-dismiss"
          className="mt-3 w-full rounded-[6px] py-2 text-center text-[13px] font-semibold text-white/60 transition-colors hover:text-white"
        >
          Not now
        </button>
      </div>
    </div>
  );
}
